import React, { useState, useRef } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useData } from '../context/DataContext';
import './Admin.css';

function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
}

const ADMIN_EMAIL    = 'admin@omcares.in';   // change after setup
const ADMIN_PASSWORD = 'omcaresutsav20';         // change after setup

export default function Admin() {
  const { productLines, companyInfo, loading, usingFirebase, seedFirebase,
          updateLine, addLine, deleteLine, updateProduct, addProduct, deleteProduct, updateCompany } = useData();

  const [authed, setAuthed]           = useState(false);
  const [email, setEmail]             = useState('');
  const [pw, setPw]                   = useState('');
  const [loginErr, setLoginErr]       = useState('');
  const [tab, setTab]                 = useState('products');
  const [activeLine, setActiveLine]   = useState(0);
  const [editProd, setEditProd]       = useState(null);
  const [showAddProd, setShowAddProd] = useState(false);
  const [showAddLine, setShowAddLine] = useState(false);
  const [confirmDel, setConfirmDel]   = useState(null);
  const [toast, setToast]             = useState('');
  const [uploading, setUploading]     = useState(false);
  const [seeding, setSeeding]         = useState(false);

  const [newProd, setNewProd] = useState({ name: '', desc: '', material: '', use: '', image: '' });
  const [newLine, setNewLine] = useState({ name: '', subtitle: '', description: '', color: '#2C5F2E', lightColor: '#e8f5e9', image: '' });
  const [localCompany, setLocalCompany] = useState(companyInfo);

  function showToast(msg, dur = 2800) { setToast(msg); setTimeout(() => setToast(''), dur); }

  // ── Login ──────────────────────────────────────────────────
  async function handleLogin(e) {
    e.preventDefault();
    setLoginErr('');
    // Simple local password check (works without Firebase Auth setup)
    if (pw === ADMIN_PASSWORD) {
      setAuthed(true);
      setLocalCompany(companyInfo);
      return;
    }
    // Also try Firebase Auth
    try {
      await signInWithEmailAndPassword(auth, email || ADMIN_EMAIL, pw);
      setAuthed(true);
      setLocalCompany(companyInfo);
    } catch {
      setLoginErr('Incorrect password. Default: omcares2025');
    }
  }

  function handleLogout() { signOut(auth).catch(() => {}); setAuthed(false); }

  // ── Image upload ────────────────────────────────────────────
  async function uploadImage(file, path) {
    // Try Firebase Storage first
    try {
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    } catch {
      // Fall back to base64
      return toBase64(file);
    }
  }

  // ── Product image ───────────────────────────────────────────
  async function handleProductImg(lineId, prodIdx, file) {
    if (!file) return;
    setUploading(true);
    showToast('⬆️ Uploading image...');
    const url = await uploadImage(file, `products/${lineId}/${Date.now()}_${file.name}`);
    await updateProduct(lineId, prodIdx, { image: url });
    setUploading(false);
    showToast('✅ Image updated!');
  }

  // ── Sector image ────────────────────────────────────────────
  async function handleLineImg(lineId, file) {
    if (!file) return;
    setUploading(true);
    showToast('⬆️ Uploading image...');
    const url = await uploadImage(file, `sectors/${lineId}/${Date.now()}_${file.name}`);
    await updateLine(lineId, { image: url });
    setUploading(false);
    showToast('✅ Sector image updated!');
  }

  // ── Seed Firebase ───────────────────────────────────────────
  async function handleSeed() {
    if (!window.confirm('This will upload all default product data to Firebase. Continue?')) return;
    setSeeding(true);
    showToast('⬆️ Uploading data to Firebase...');
    const ok = await seedFirebase();
    setSeeding(false);
    showToast(ok ? '✅ Firebase seeded successfully!' : '❌ Error seeding. Check Firebase config.');
  }

  const currentLine = productLines[activeLine];

  // ── LOGIN SCREEN ─────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="admin-login">
        <div className="login-card">
          <div className="login-logo">
            <img src="/images/logo.png" alt="OM Cares" onError={e => e.target.style.display='none'}/>
          </div>
          <h1>OM Cares</h1>
          <p className="login-sub">Website Admin Panel</p>
          <form onSubmit={handleLogin}>
            <div className="lfield">
              <label>Password</label>
              <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="Enter admin password" autoFocus/>
            </div>
            {loginErr && <div className="lerr">{loginErr}</div>}
            <button type="submit" className="login-btn">Login →</button>
          </form>
        </div>
      </div>
    );
  }

  // ── ADMIN DASHBOARD ──────────────────────────────────────────
  return (
    <div className="admin-wrap">

      {/* Toast */}
      {toast && <div className="a-toast">{toast}</div>}

      {/* Confirm delete modal */}
      {confirmDel && (
        <div className="a-overlay" onClick={() => setConfirmDel(null)}>
          <div className="a-confirm" onClick={e => e.stopPropagation()}>
            <h3>⚠️ Confirm Delete</h3>
            <p>{confirmDel.msg}</p>
            <div className="a-confirm-btns">
              <button className="btn-cancel" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="btn-del-confirm" onClick={() => { confirmDel.action(); setConfirmDel(null); }}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="a-header">
        <div className="a-header-left">
          <div className="a-brand">
            <span className="a-brand-dot"/>
            <span>OM Cares Admin</span>
          </div>
          <span className="a-status">
            {usingFirebase
              ? <span className="status-live">🟢 Live — Connected to Firebase</span>
              : <span className="status-local">🟡 Local — Static data (Firebase not configured)</span>}
          </span>
        </div>
        <div className="a-header-right">
          {!usingFirebase && (
            <button className="btn-seed" onClick={handleSeed} disabled={seeding}>
              {seeding ? '⬆️ Uploading...' : '⬆️ Upload to Firebase'}
            </button>
          )}
          <a href="/" target="_blank" className="btn-view">👁 View Website</a>
          <button className="btn-logout" onClick={handleLogout}>Logout</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="a-tabs">
        <button className={`a-tab${tab==='products'?' on':''}`} onClick={() => setTab('products')}>📦 Products & Sectors</button>
        <button className={`a-tab${tab==='company'?' on':''}`} onClick={() => setTab('company')}>🏢 Company Info</button>
        <button className={`a-tab${tab==='guide'?' on':''}`} onClick={() => setTab('guide')}>📖 Setup Guide</button>
      </div>

      {loading && <div className="a-loading">Loading data...</div>}

      {/* ── PRODUCTS TAB ── */}
      {!loading && tab === 'products' && (
        <div className="a-products">

          {/* Sector sidebar */}
          <div className="a-sidebar">
            <div className="a-sidebar-title">
              Sectors <span className="a-badge">{productLines.length}</span>
            </div>
            {productLines.map((line, li) => (
              <div key={li}
                className={`a-sector-item${activeLine === li ? ' on' : ''}`}
                style={{ borderLeftColor: activeLine === li ? line.color : 'transparent' }}
                onClick={() => { setActiveLine(li); setShowAddProd(false); setEditProd(null); }}>
                <div className="asi-name" style={{ color: activeLine === li ? line.color : '' }}>{line.name}</div>
                <div className="asi-count">{line.products?.length || 0} products</div>
              </div>
            ))}
            <button className="btn-add-sector" onClick={() => setShowAddLine(true)}>+ Add New Sector</button>
          </div>

          {/* Sector content */}
          <div className="a-sector-content">
            {currentLine && (
              <>
                {/* Sector header editor */}
                <div className="a-section-box">
                  <div className="a-section-head">
                    <h2 style={{ color: currentLine.color }}>✏️ {currentLine.name}</h2>
                    <button className="btn-danger-sm" onClick={() => setConfirmDel({ msg: `Delete "${currentLine.name}" and ALL its products?`, action: () => { deleteLine(currentLine.id || currentLine._id); setActiveLine(0); } })}>🗑 Delete Sector</button>
                  </div>
                  <div className="a-grid-3">
                    <div className="a-field">
                      <label>Sector Name</label>
                      <input value={currentLine.name} onChange={e => updateLine(currentLine.id || currentLine._id, { name: e.target.value })}/>
                    </div>
                    <div className="a-field">
                      <label>Subtitle</label>
                      <input value={currentLine.subtitle} onChange={e => updateLine(currentLine.id || currentLine._id, { subtitle: e.target.value })}/>
                    </div>
                    <div className="a-field">
                      <label>Theme Color</label>
                      <div className="color-row">
                        <input type="color" value={currentLine.color} onChange={e => updateLine(currentLine.id || currentLine._id, { color: e.target.value })}/>
                        <input value={currentLine.color} onChange={e => updateLine(currentLine.id || currentLine._id, { color: e.target.value })}/>
                      </div>
                    </div>
                  </div>
                  <div className="a-field" style={{ marginTop: 12 }}>
                    <label>Description</label>
                    <textarea value={currentLine.description} rows={2} onChange={e => updateLine(currentLine.id || currentLine._id, { description: e.target.value })}/>
                  </div>
                  <div className="a-field" style={{ marginTop: 12 }}>
                    <label>Sector Banner Image</label>
                    <div className="img-row">
                      <div className="img-prev"><img src={currentLine.image} alt="" onError={e => e.target.style.opacity=0}/></div>
                      <div style={{ flex: 1 }}>
                        <input type="file" accept="image/*" id={`line-img-${activeLine}`} style={{ display:'none' }}
                          onChange={e => handleLineImg(currentLine.id || currentLine._id, e.target.files[0])}/>
                        <label htmlFor={`line-img-${activeLine}`} className="btn-upload">📁 Upload Image</label>
                        <div className="img-or">or paste image URL:</div>
                        <input value={currentLine.image?.startsWith('data:') ? '[Uploaded image]' : currentLine.image || ''}
                          onChange={e => updateLine(currentLine.id || currentLine._id, { image: e.target.value })}
                          placeholder="https://..." style={{ width:'100%', marginTop:4 }}/>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Products */}
                <div className="a-section-box">
                  <div className="a-section-head">
                    <h3>Products in {currentLine.name} <span className="a-badge" style={{ background: currentLine.color }}>{currentLine.products?.length || 0}</span></h3>
                    <button className="btn-add" style={{ background: currentLine.color }}
                      onClick={() => { setShowAddProd(true); setEditProd(null); }}>+ Add Product</button>
                  </div>

                  {/* Add product form */}
                  {showAddProd && (
                    <div className="a-add-form">
                      <h4>➕ New Product in {currentLine.name}</h4>
                      <div className="a-grid-2">
                        <div className="a-field"><label>Product Name *</label><input value={newProd.name} onChange={e => setNewProd({...newProd, name: e.target.value})} placeholder="e.g. Surgical Gloves"/></div>
                        <div className="a-field"><label>Material</label><input value={newProd.material} onChange={e => setNewProd({...newProd, material: e.target.value})} placeholder="e.g. Natural Latex"/></div>
                      </div>
                      <div className="a-field"><label>Description</label><textarea value={newProd.desc} rows={2} onChange={e => setNewProd({...newProd, desc: e.target.value})} placeholder="Product description..."/></div>
                      <div className="a-field"><label>Application</label><input value={newProd.use} onChange={e => setNewProd({...newProd, use: e.target.value})} placeholder="e.g. Hospitals, clinics"/></div>
                      <div className="a-field">
                        <label>Image</label>
                        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                          <input type="file" accept="image/*" id="new-prod-file" style={{ display:'none' }}
                            onChange={async e => { const url = await uploadImage(e.target.files[0], `products/new/${Date.now()}`); setNewProd({...newProd, image: url}); showToast('✅ Image ready'); }}/>
                          <label htmlFor="new-prod-file" className="btn-upload">📁 Upload</label>
                          <span style={{ color:'#777', fontSize:'.78rem' }}>or URL:</span>
                          <input value={newProd.image?.startsWith('data:') ? '[Uploaded]' : newProd.image} onChange={e => setNewProd({...newProd, image: e.target.value})} placeholder="https://..." style={{ flex:1 }}/>
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8, marginTop:12 }}>
                        <button className="btn-add" style={{ background: currentLine.color }} onClick={async () => {
                          if (!newProd.name.trim()) { showToast('⚠️ Name required'); return; }
                          await addProduct(currentLine.id || currentLine._id, { ...newProd });
                          setNewProd({ name:'', desc:'', material:'', use:'', image:'' });
                          setShowAddProd(false);
                          showToast('✅ Product added!');
                        }}>✅ Add Product</button>
                        <button className="btn-cancel" onClick={() => setShowAddProd(false)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Products grid */}
                  <div className="a-prod-grid">
                    {(currentLine.products || []).map((prod, pi) => (
                      <div key={pi} className={`a-prod-card${editProd === pi ? ' editing' : ''}`}>
                        {/* Image */}
                        <div className="apc-img">
                          <img src={prod.image} alt={prod.name}
                            onError={e => { e.target.src='https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?w=200&q=60'; }}/>
                          <div className="apc-img-overlay">
                            <input type="file" accept="image/*" id={`pi-${activeLine}-${pi}`} style={{ display:'none' }}
                              onChange={e => handleProductImg(currentLine.id || currentLine._id, pi, e.target.files[0])}/>
                            <label htmlFor={`pi-${activeLine}-${pi}`} className="apc-change-btn">📷 Change</label>
                          </div>
                        </div>
                        {/* Body */}
                        <div className="apc-body">
                          <div className="apc-num">{String(pi+1).padStart(2,'0')}</div>
                          {editProd === pi ? (
                            <div className="apc-edit">
                              <input className="apc-name-inp" value={prod.name} onChange={e => updateProduct(currentLine.id||currentLine._id, pi, { name: e.target.value })} placeholder="Name"/>
                              <textarea value={prod.desc} rows={2} onChange={e => updateProduct(currentLine.id||currentLine._id, pi, { desc: e.target.value })} placeholder="Description"/>
                              <input value={prod.material} onChange={e => updateProduct(currentLine.id||currentLine._id, pi, { material: e.target.value })} placeholder="Material"/>
                              <input value={prod.use} onChange={e => updateProduct(currentLine.id||currentLine._id, pi, { use: e.target.value })} placeholder="Application"/>
                              <input value={prod.image?.startsWith('data:')||prod.image?.includes('firebase') ? '[Uploaded image]' : prod.image||''} onChange={e => updateProduct(currentLine.id||currentLine._id, pi, { image: e.target.value })} placeholder="Image URL"/>
                              <button className="apc-done" style={{ background: currentLine.color }} onClick={() => { setEditProd(null); showToast('✅ Saved!'); }}>✓ Done</button>
                            </div>
                          ) : (
                            <>
                              <div className="apc-name">{prod.name}</div>
                              <div className="apc-material">{prod.material}</div>
                              <div className="apc-actions">
                                <button className="apc-edit-btn" onClick={() => setEditProd(pi)}>✏️ Edit</button>
                                <button className="apc-del-btn" onClick={() => setConfirmDel({ msg: `Delete "${prod.name}"?`, action: () => { deleteProduct(currentLine.id||currentLine._id, pi); showToast('🗑️ Deleted.'); } })}>🗑</button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── ADD SECTOR MODAL ── */}
      {showAddLine && (
        <div className="a-overlay" onClick={() => setShowAddLine(false)}>
          <div className="a-modal" onClick={e => e.stopPropagation()}>
            <h3>➕ Add New Sector</h3>
            <div className="a-grid-2">
              <div className="a-field"><label>Sector Name *</label><input value={newLine.name} onChange={e => setNewLine({...newLine, name: e.target.value})} placeholder="e.g. DentaCare"/></div>
              <div className="a-field"><label>Subtitle</label><input value={newLine.subtitle} onChange={e => setNewLine({...newLine, subtitle: e.target.value})} placeholder="e.g. Dental Care"/></div>
            </div>
            <div className="a-field"><label>Description</label><textarea value={newLine.description} rows={2} onChange={e => setNewLine({...newLine, description: e.target.value})} placeholder="Sector description..."/></div>
            <div className="a-grid-2">
              <div className="a-field"><label>Theme Color</label><div className="color-row"><input type="color" value={newLine.color} onChange={e => setNewLine({...newLine, color: e.target.value})}/><input value={newLine.color} onChange={e => setNewLine({...newLine, color: e.target.value})}/></div></div>
              <div className="a-field"><label>Image URL</label><input value={newLine.image} onChange={e => setNewLine({...newLine, image: e.target.value})} placeholder="https://..."/></div>
            </div>
            <div style={{ display:'flex', gap:8, marginTop:14 }}>
              <button className="btn-add" style={{ background:'var(--green)' }} onClick={async () => {
                if (!newLine.name.trim()) { showToast('⚠️ Name required'); return; }
                await addLine({ ...newLine, icon: newLine.name.toLowerCase().replace(/\s+/g,'-') });
                setNewLine({ name:'', subtitle:'', description:'', color:'#2C5F2E', lightColor:'#e8f5e9', image:'' });
                setShowAddLine(false);
                showToast('✅ Sector added!');
              }}>✅ Add Sector</button>
              <button className="btn-cancel" onClick={() => setShowAddLine(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── COMPANY TAB ── */}
      {!loading && tab === 'company' && (
        <div className="a-company">
          <div className="a-section-box">
            <h2>🏢 Company Information</h2>
            <p className="a-hint">This updates contact info across the entire website — footer, contact page, WhatsApp button.</p>
            <div className="a-grid-2">
              <div className="a-field"><label>Company Name</label><input value={localCompany.name||''} onChange={e => setLocalCompany({...localCompany, name: e.target.value})}/></div>
              <div className="a-field"><label>Tagline</label><input value={localCompany.tagline||''} onChange={e => setLocalCompany({...localCompany, tagline: e.target.value})}/></div>
              <div className="a-field"><label>Email</label><input value={localCompany.email||''} onChange={e => setLocalCompany({...localCompany, email: e.target.value})}/></div>
              <div className="a-field"><label>WhatsApp Number (with country code, no +)</label><input value={localCompany.whatsapp||''} onChange={e => setLocalCompany({...localCompany, whatsapp: e.target.value})} placeholder="919967150127"/></div>
              <div className="a-field"><label>Phone 1</label><input value={localCompany.phone1||''} onChange={e => setLocalCompany({...localCompany, phone1: e.target.value})}/></div>
              <div className="a-field"><label>Phone 2</label><input value={localCompany.phone2||''} onChange={e => setLocalCompany({...localCompany, phone2: e.target.value})}/></div>
            </div>
            <div className="a-field" style={{ marginTop:12 }}><label>Address</label><textarea value={localCompany.address||''} rows={2} onChange={e => setLocalCompany({...localCompany, address: e.target.value})}/></div>
            <div className="a-field" style={{ marginTop:12 }}>
              <label>Certifications (comma separated)</label>
              <input value={(localCompany.certifications||[]).join(', ')} onChange={e => setLocalCompany({...localCompany, certifications: e.target.value.split(',').map(s=>s.trim())})} placeholder="ISO 9001:2015, FDA Approved, MSME Registered, Make in India"/>
            </div>
            <button className="btn-save-company" onClick={async () => { await updateCompany(localCompany); showToast('✅ Company info saved!'); }}>💾 Save Company Info</button>
          </div>
        </div>
      )}

      {/* ── GUIDE TAB ── */}
      {tab === 'guide' && (
        <div className="a-company">
          <div className="a-section-box">
            <h2>📖 Firebase Setup Guide</h2>
            <p className="a-hint">Follow these steps to connect the admin panel to Firebase so changes are saved permanently.</p>
            <div className="guide-steps">
              <div className="guide-step"><div className="gs-num">1</div><div><div className="gs-title">Create Firebase Project</div><div className="gs-desc">Go to <a href="https://console.firebase.google.com" target="_blank" rel="noreferrer">console.firebase.google.com</a> → Create project → Name it "omcares"</div></div></div>
              <div className="guide-step"><div className="gs-num">2</div><div><div className="gs-title">Enable Firestore Database</div><div className="gs-desc">Firebase Console → Build → Firestore Database → Create database → Start in test mode</div></div></div>
              <div className="guide-step"><div className="gs-num">3</div><div><div className="gs-title">Enable Storage</div><div className="gs-desc">Firebase Console → Build → Storage → Get started → Test mode</div></div></div>
              <div className="guide-step"><div className="gs-num">4</div><div><div className="gs-title">Get Your Config</div><div className="gs-desc">Project Settings → Your apps → Add Web App → Copy the firebaseConfig object</div></div></div>
              <div className="guide-step"><div className="gs-num">5</div><div><div className="gs-title">Update firebase.js</div><div className="gs-desc">Open <code>src/firebase.js</code> → Replace all YOUR_* values with your real config values</div></div></div>
              <div className="guide-step"><div className="gs-num">6</div><div><div className="gs-title">Seed Data</div><div className="gs-desc">Come back to Admin Panel → Click "Upload to Firebase" button → All products go to Firebase</div></div></div>
              <div className="guide-step"><div className="gs-num">7</div><div><div className="gs-title">Change Admin Password</div><div className="gs-desc">Open <code>src/pages/Admin.jsx</code> → Line 10 → Change <code>ADMIN_PASSWORD</code> to something secure</div></div></div>
            </div>
            <div className="guide-note">💡 Once Firebase is connected, all changes in this admin panel are saved permanently and show on the website instantly — no developer needed!</div>
          </div>
        </div>
      )}
    </div>
  );
}
